import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import * as Routes from '@/modules/routes';
import { formatLocalTimestampWithFormat } from '@/components/common/Utils';
import useStudentSeatSyncStore from '@/stores/useStudentSeatSyncStore';

interface StudentSeatToggleProps {
  attribute: 'reading_level_a_enabled' | 'spanish_enabled' | 'audio_enabled' | 'main_ideas_enabled';
  initialValue: boolean;
  studentSeatId: number | string;
}

const StudentSeatToggleComponent = ({ attribute, initialValue, studentSeatId }: StudentSeatToggleProps) => {
  const [isEnabled, setIsEnabled] = useState(initialValue);
  const lastUpdate = useStudentSeatSyncStore(state => state.lastUpdate);
  const notify = useStudentSeatSyncStore(state => state.notify);

  const getTimeContainer = () => document.getElementById('updated-time-container');

  const updateTime = (dateTime: string) => {
    const timeContainer = getTimeContainer();

    if (!timeContainer) return;
    timeContainer.innerHTML = `Autosaved on: ${formatLocalTimestampWithFormat(dateTime, 'MMMM DD, YYYY hh:mm A')}`;
  };

  // Sync when the ReadingLevelSelect auto-corrects reading_level_a_enabled
  useEffect(() => {
    if (attribute !== 'reading_level_a_enabled') return;
    if (!lastUpdate || lastUpdate.studentSeatId !== String(studentSeatId)) return;
    if (lastUpdate.readingLevelAEnabled === undefined) return;

    setIsEnabled(lastUpdate.readingLevelAEnabled);
  }, [attribute, lastUpdate, studentSeatId]);

  const { isLoading, mutateAsync } = useMutation({
    mutationFn: (value: boolean) => axios.patch(
      Routes.plato_api_student_seat_path(studentSeatId),
      { student_seat: { [attribute]: value } },
    ),
    mutationKey: ['updateStudentSeat', studentSeatId, attribute],
  });

  const handleToggle = async () => {
    const newValue = !isEnabled;
    const previousValue = isEnabled;
    setIsEnabled(newValue);

    try {
      const result = await mutateAsync(newValue);
      const data = result.data?.data;

      const updatedAtTime = data?.time_updated_at || data?.updated_at;
      updateTime(updatedAtTime);

      if (attribute === 'reading_level_a_enabled') {
        const correctedValue = data?.reading_level_a_enabled;
        if (correctedValue !== undefined && correctedValue !== newValue) setIsEnabled(correctedValue);

        notify({
          readingLevel: data?.reading_level,
          readingLevelAEnabled: data?.reading_level_a_enabled,
          studentSeatId: String(studentSeatId),
        });
      }
    } catch (error) {
      console.error(`Error updating ${attribute}:`, error);
      setIsEnabled(previousValue);
    }
  };

  return (
    <input
      checked={isEnabled}
      disabled={isLoading}
      type="checkbox"
      onChange={handleToggle}
    />
  );
};

const StudentSeatToggle = (props: StudentSeatToggleProps) => (
  <ReactQueryProvider>
    <StudentSeatToggleComponent {...props} />
  </ReactQueryProvider>
);

export default StudentSeatToggle;
